import TruncatedText from '@app/src/components/Ui/TruncatedText'
import type { Provider } from '@app/src/types/organizations'
import { Divider, Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import SimpleRiskChip from './SimpleRiskChip'
import type { ProviderRiskIndex } from './useProviderRiskIndices'

interface AssessmentLocationAndRisksProps {
  provider: Provider | undefined
  riskIndices: ProviderRiskIndex[]
}

const RiskIndexValue: React.FC<{ index: ProviderRiskIndex }> = ({ index }) => {
  const { formatMessage } = useIntl()

  if (!index.isActivated) {
    return (
      <TruncatedText
        color="text.disabled"
        fontSize="small"
        text={formatMessage({ id: 'companyProfile.inactiveIndex' })}
        line={1}
      />
    )
  }
  if (!index.status) {
    return (
      <TruncatedText
        color="text.disabled"
        fontSize="small"
        text={formatMessage({ id: 'companyProfile.noData' })}
        line={1}
      />
    )
  }
  return <SimpleRiskChip riskStatus={index.status} />
}

const AssessmentLocationAndRisks: React.FC<AssessmentLocationAndRisksProps> = ({ provider, riskIndices }) => {
  const { formatMessage } = useIntl()
  const country = provider?.country

  return (
    <Stack direction="row" spacing={3} alignItems="flex-start" width="100%">
      <Stack width={200} spacing={0.5}>
        <Typography variant="subtitle2">{formatMessage({ id: 'schemas.organization.location' })}</Typography>
        <Stack direction="row" alignItems="center" spacing={1} minHeight={32}>
          {country ? (
            <>
              <img
                src={`https://flagcdn.com/w320/${country.countryCode.toLowerCase()}.png`}
                alt={country.name}
                style={{ width: '1.5em', height: '1em' }}
              />
              <Typography>{country.name}</Typography>
            </>
          ) : (
            <Typography color="text.secondary">{formatMessage({ id: 'companyProfile.noData' })}</Typography>
          )}
        </Stack>
      </Stack>
      {riskIndices.length > 0 && (
        <>
          <Divider flexItem orientation="vertical" />
          <Stack direction="row" flexWrap="wrap" gap={3} alignItems="flex-start" flex={1}>
            {riskIndices.map(index => (
              <Stack key={index.riskType.id} minWidth={120} spacing={0.5}>
                <TruncatedText
                  variant="subtitle2"
                  color={index.isActivated ? 'text.primary' : 'text.disabled'}
                  text={formatMessage({ id: `schemas.risk.riskIndexes.${index.riskType.source}.label` })}
                  line={1}
                />
                <RiskIndexValue index={index} />
              </Stack>
            ))}
          </Stack>
        </>
      )}
    </Stack>
  )
}

export default AssessmentLocationAndRisks
